import {
  applyTheme,
  readThemePreference,
  THEME_CHANGE_EVENT,
  writeThemePreference,
  type ResolvedTheme,
} from './theme.tsx'

const runtime = new AbortController()
const { signal } = runtime

window.addEventListener(
  'pagehide',
  (event) => {
    if (!event.persisted) runtime.abort()
  },
  { signal },
)

/**
 * Runs a theme swap with CSS transitions suppressed, so every color flips in
 * the same frame instead of fading at each element's own transition speed.
 */
function withoutTransitions(run: () => void) {
  const style = document.createElement('style')
  style.textContent = '*,*::before,*::after{transition:none!important}'
  document.head.append(style)
  try {
    run()
  } finally {
    void getComputedStyle(document.body).opacity
    requestAnimationFrame(() => requestAnimationFrame(() => style.remove()))
  }
}

function initializeTheme() {
  let resolved: ResolvedTheme = document.documentElement.classList.contains(
    'dark',
  )
    ? 'dark'
    : 'light'
  try {
    resolved = applyTheme(readThemePreference())
  } catch {
    document.documentElement.dataset.theme = resolved
  }

  const updateButtons = () => {
    for (const button of document.querySelectorAll<HTMLButtonElement>(
      '[data-theme-toggle]',
    )) {
      const next = resolved === 'dark' ? 'light' : 'dark'
      button.setAttribute('aria-label', `Switch to ${next} theme`)
      button.title = `Switch to ${next} theme`
    }
  }
  updateButtons()

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target
      if (!(target instanceof Element)) return
      if (!target.closest('[data-theme-toggle]')) return
      withoutTransitions(() => {
        resolved = writeThemePreference(resolved === 'dark' ? 'light' : 'dark')
        updateButtons()
      })
    },
    { signal },
  )

  window.addEventListener(
    THEME_CHANGE_EVENT,
    (event) => {
      if (event instanceof CustomEvent) {
        const next = event.detail?.resolved
        if (next === 'light' || next === 'dark') resolved = next
      }
      updateButtons()
    },
    { signal },
  )

  const media = window.matchMedia('(prefers-color-scheme: dark)')
  media.addEventListener(
    'change',
    () => {
      try {
        if (readThemePreference() !== 'system') return
        withoutTransitions(() => {
          resolved = applyTheme('system')
          updateButtons()
        })
      } catch {
        // Storage can be unavailable in privacy-restricted contexts.
      }
    },
    { signal },
  )
}

function initializeHeader() {
  const header = document.querySelector<HTMLElement>('[data-header]')
  const measured = header?.querySelector<HTMLElement>('[data-header-measure]')
  if (!header || !measured) return

  const root = document.documentElement
  const avatarSpacer = header.querySelector<HTMLElement>('[data-avatar-spacer]')
  const home = header.dataset.home === 'true'
  let initial = true
  let frame = 0

  const set = (name: string, value: string) =>
    root.style.setProperty(name, value)
  const remove = (name: string) => root.style.removeProperty(name)
  const clamp = (value: number, a: number, b: number) =>
    Math.min(Math.max(value, Math.min(a, b)), Math.max(a, b))

  const update = () => {
    frame = 0
    const downDelay = avatarSpacer?.offsetTop ?? 0
    const upDelay = 64
    const { top, height } = measured.getBoundingClientRect()
    const scrollY = clamp(
      window.scrollY,
      0,
      Math.max(0, document.body.scrollHeight - window.innerHeight),
    )

    if (initial) set('--header-position', 'sticky')
    set('--content-offset', `${downDelay}px`)

    if (initial || scrollY < downDelay) {
      set('--header-height', `${downDelay + height}px`)
      set('--header-mb', `${-downDelay}px`)
    } else if (top + height < -upDelay) {
      const offset = Math.max(height, scrollY - upDelay)
      set('--header-height', `${offset}px`)
      set('--header-mb', `${height - offset}px`)
    } else if (top === 0) {
      set('--header-height', `${scrollY + height}px`)
      set('--header-mb', `${-scrollY}px`)
    }

    if (top === 0 && scrollY > 0 && scrollY >= downDelay) {
      set('--header-inner-position', 'fixed')
      remove('--header-top')
      remove('--avatar-top')
    } else {
      remove('--header-inner-position')
      set('--header-top', '0px')
      set('--avatar-top', '0px')
    }

    if (home && downDelay > 0) {
      const fromScale = 1
      const toScale = 36 / 64
      const toX = 2 / 16
      const remaining = downDelay - window.scrollY
      const scale = clamp(
        (remaining * (fromScale - toScale)) / downDelay + toScale,
        fromScale,
        toScale,
      )
      const x = clamp((remaining * -toX) / downDelay + toX, 0, toX)
      set(
        '--avatar-image-transform',
        `translate3d(${x}rem, 0, 0) scale(${scale})`,
      )
      const borderScale = 1 / (toScale / scale)
      const borderX = (-toX + x) * borderScale
      set(
        '--avatar-border-transform',
        `translate3d(${borderX}rem, 0, 0) scale(${borderScale})`,
      )
      set('--avatar-border-opacity', scale === toScale ? '1' : '0')
    }
    initial = false
  }

  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(update)
  }
  update()
  window.addEventListener('scroll', schedule, { passive: true, signal })
  window.addEventListener('resize', schedule, { signal })
  signal.addEventListener(
    'abort',
    () => {
      if (frame) cancelAnimationFrame(frame)
    },
    { once: true },
  )
}

function initializeMobileNavigation() {
  const dialog = document.querySelector<HTMLDialogElement>('[data-mobile-nav]')
  const openButton = document.querySelector<HTMLButtonElement>(
    '[data-mobile-nav-open]',
  )
  const panel = dialog?.querySelector<HTMLElement>('[data-mobile-nav-panel]')
  if (!dialog || !openButton || !panel) return

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
  let restoreOverflow = ''

  const setExpanded = (expanded: boolean) => {
    openButton.setAttribute('aria-expanded', String(expanded))
  }

  const close = async () => {
    if (!dialog.open) return
    if (!reducedMotion.matches) {
      try {
        await panel.animate(
          [
            { opacity: 1, transform: 'scale(1)' },
            { opacity: 0, transform: 'scale(.95)' },
          ],
          { duration: 120, easing: 'ease-in', fill: 'forwards' },
        ).finished
      } catch {
        // An interrupted animation should still close the dialog.
      }
    }
    dialog.close()
    document.documentElement.style.overflow = restoreOverflow
    openButton.focus()
  }

  openButton.addEventListener(
    'click',
    () => {
      restoreOverflow = document.documentElement.style.overflow
      document.documentElement.style.overflow = 'hidden'
      dialog.showModal()
      setExpanded(true)
      if (!reducedMotion.matches) {
        panel.animate(
          [
            { opacity: 0, transform: 'scale(.95)' },
            { opacity: 1, transform: 'scale(1)' },
          ],
          { duration: 150, easing: 'ease-out' },
        )
      }
      dialog.querySelector<HTMLElement>('[data-mobile-nav-close]')?.focus()
    },
    { signal },
  )
  dialog.addEventListener('close', () => setExpanded(false), { signal })
  dialog.addEventListener(
    'click',
    (event) => {
      const target = event.target
      if (!(target instanceof Element)) return
      if (target.closest('a[href]')) {
        dialog.close()
        document.documentElement.style.overflow = restoreOverflow
        return
      }
      if (
        target.closest('[data-mobile-nav-close]') ||
        (target.closest('[data-mobile-nav-backdrop]') &&
          !target.closest('[data-mobile-nav-panel]'))
      ) {
        void close()
      }
    },
    { signal },
  )
  dialog.addEventListener(
    'cancel',
    (event) => {
      event.preventDefault()
      void close()
    },
    { signal },
  )
  signal.addEventListener(
    'abort',
    () => {
      document.documentElement.style.overflow = restoreOverflow
    },
    { once: true },
  )
}

interface NavigationRecord {
  from: string
  to: string
  at: number
}

const NAVIGATION_KEY = 'portfolio:navigation'

function readNavigationRecord(): NavigationRecord | undefined {
  try {
    const raw = sessionStorage.getItem(NAVIGATION_KEY)
    sessionStorage.removeItem(NAVIGATION_KEY)
    if (!raw) return
    const value = JSON.parse(raw) as Partial<NavigationRecord>
    if (
      typeof value.from === 'string' &&
      typeof value.to === 'string' &&
      typeof value.at === 'number' &&
      Date.now() - value.at < 10 * 60_000
    ) {
      return value as NavigationRecord
    }
  } catch {
    // Ignore stale or inaccessible session data.
  }
}

function eligibleNavigation(event: MouseEvent, anchor: HTMLAnchorElement) {
  return (
    event.button === 0 &&
    !event.defaultPrevented &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey &&
    !anchor.download &&
    (!anchor.target || anchor.target === '_self')
  )
}

function initializeNavigation() {
  const record = readNavigationRecord()
  const current = `${location.pathname}${location.search}`
  const backLink = document.querySelector<HTMLAnchorElement>(
    '[data-article-back]',
  )
  const validRecord = record?.to === current ? record : undefined

  if (backLink && validRecord) {
    backLink.href = validRecord.from
    backLink.addEventListener(
      'click',
      (event) => {
        if (
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        ) {
          return
        }
        event.preventDefault()
        history.back()
      },
      { signal },
    )
  }

  document.addEventListener(
    'click',
    (event) => {
      if (!(event instanceof MouseEvent)) return
      const target = event.target
      if (!(target instanceof Element)) return
      const anchor = target.closest<HTMLAnchorElement>('a[href]')
      if (!anchor || !eligibleNavigation(event, anchor)) return
      const destination = new URL(anchor.href, location.href)
      if (destination.origin !== location.origin) return
      const to = `${destination.pathname}${destination.search}`
      if (to === current) return
      try {
        sessionStorage.setItem(
          NAVIGATION_KEY,
          JSON.stringify({
            from: `${location.pathname}${location.search}${location.hash}`,
            to,
            at: Date.now(),
          } satisfies NavigationRecord),
        )
      } catch {
        // Navigation should never depend on storage access.
      }
    },
    { signal },
  )
}

function initializeIntentPrefetch() {
  const connection = (
    navigator as Navigator & {
      connection?: { saveData?: boolean; effectiveType?: string }
    }
  ).connection
  if (
    connection?.saveData ||
    connection?.effectiveType === 'slow-2g' ||
    connection?.effectiveType === '2g'
  ) {
    return
  }

  const prefetched = new Set<string>()
  const hoverTimers = new Map<HTMLAnchorElement, number>()
  const prefetch = (anchor: HTMLAnchorElement) => {
    if (anchor.dataset.intentPrefetch !== 'true') return
    if (anchor.download || (anchor.target && anchor.target !== '_self')) return
    const destination = new URL(anchor.href, location.href)
    if (destination.origin !== location.origin) return
    destination.hash = ''
    if (
      destination.pathname === location.pathname &&
      destination.search === location.search
    ) {
      return
    }
    const href = destination.href
    if (prefetched.has(href)) return
    prefetched.add(href)
    const hint = document.createElement('link')
    hint.rel = 'prefetch'
    hint.as = 'document'
    hint.href = href
    hint.dataset.runtimePrefetch = 'true'
    document.head.append(hint)
  }

  document.addEventListener(
    'focusin',
    (event) => {
      const target = event.target
      if (!(target instanceof Element)) return
      const anchor = target.closest<HTMLAnchorElement>(
        'a[data-intent-prefetch="true"]',
      )
      if (anchor) prefetch(anchor)
    },
    { signal },
  )
  document.addEventListener(
    'pointerover',
    (event) => {
      const target = event.target
      if (!(target instanceof Element)) return
      const anchor = target.closest<HTMLAnchorElement>(
        'a[data-intent-prefetch="true"]',
      )
      if (!anchor) return
      if (
        event.relatedTarget instanceof Node &&
        anchor.contains(event.relatedTarget)
      ) {
        return
      }
      const pending = hoverTimers.get(anchor)
      if (pending) window.clearTimeout(pending)
      const timer = window.setTimeout(() => {
        hoverTimers.delete(anchor)
        prefetch(anchor)
      }, 80)
      hoverTimers.set(anchor, timer)
    },
    { signal },
  )
  document.addEventListener(
    'pointerout',
    (event) => {
      const target = event.target
      if (!(target instanceof Element)) return
      const anchor = target.closest<HTMLAnchorElement>(
        'a[data-intent-prefetch="true"]',
      )
      if (!anchor) return
      if (
        event.relatedTarget instanceof Node &&
        anchor.contains(event.relatedTarget)
      ) {
        return
      }
      const timer = hoverTimers.get(anchor)
      if (timer) window.clearTimeout(timer)
      hoverTimers.delete(anchor)
    },
    { signal },
  )
  signal.addEventListener(
    'abort',
    () => {
      for (const timer of hoverTimers.values()) window.clearTimeout(timer)
      hoverTimers.clear()
      document
        .querySelectorAll('link[data-runtime-prefetch]')
        .forEach((hint) => hint.remove())
    },
    { once: true },
  )
}

function initializeTilt() {
  if (
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    window.matchMedia('(pointer: coarse)').matches
  ) {
    return
  }

  for (const element of document.querySelectorAll<HTMLElement>(
    '[data-tilt="true"]',
  )) {
    const scale = Number(element.dataset.tiltScale ?? '1.02')
    const intensity = Number(element.dataset.tiltIntensity ?? '10')
    let frame = 0
    let x = 0
    let y = 0
    let resetAnimation: Animation | undefined
    let easeTimer = 0

    // Ease into the first tilt, then drop the transition so pointer tracking
    // stays immediate.
    const clearEase = () => {
      window.clearTimeout(easeTimer)
      easeTimer = 0
      element.style.transition = ''
    }

    const render = () => {
      frame = 0
      element.style.transform = `perspective(1000px) scale(${scale}) rotateX(${-y * intensity}deg) rotateY(${x * intensity}deg)`
    }
    element.addEventListener(
      'pointerenter',
      (event) => {
        if (event.pointerType && event.pointerType !== 'mouse') return
        window.clearTimeout(easeTimer)
        element.style.transition = 'transform 150ms cubic-bezier(.2,.8,.2,1)'
        easeTimer = window.setTimeout(clearEase, 150)
      },
      { signal },
    )
    element.addEventListener(
      'pointermove',
      (event) => {
        if (event.pointerType && event.pointerType !== 'mouse') return
        resetAnimation?.cancel()
        resetAnimation = undefined
        const rect = element.getBoundingClientRect()
        x = ((event.clientX - rect.left) / rect.width) * 2 - 1
        y = ((event.clientY - rect.top) / rect.height) * 2 - 1
        if (!frame) frame = requestAnimationFrame(render)
      },
      { signal },
    )
    element.addEventListener(
      'pointerleave',
      () => {
        if (frame) cancelAnimationFrame(frame)
        frame = 0
        resetAnimation?.cancel()
        const from = getComputedStyle(element).transform
        clearEase()
        const animation = element.animate(
          [
            { transform: from === 'none' ? undefined : from },
            { transform: 'perspective(1000px) scale(1) rotateX(0) rotateY(0)' },
          ],
          {
            duration: 300,
            easing: 'cubic-bezier(.2,.8,.2,1)',
            fill: 'forwards',
          },
        )
        resetAnimation = animation
        void animation.finished
          .then(() => {
            if (resetAnimation !== animation) return
            element.style.transition = ''
            element.style.transform =
              'perspective(1000px) scale(1) rotateX(0) rotateY(0)'
            animation.cancel()
            resetAnimation = undefined
          })
          .catch(() => {
            if (resetAnimation === animation) resetAnimation = undefined
          })
      },
      { signal },
    )
    signal.addEventListener(
      'abort',
      () => {
        if (frame) cancelAnimationFrame(frame)
        window.clearTimeout(easeTimer)
        resetAnimation?.cancel()
        for (const animation of element.getAnimations()) animation.cancel()
      },
      { once: true },
    )
  }
}

const commandHelp = `
  <div class="space-y-2">
    <p class="font-bold text-green-400">Available Commands:</p>
    <div class="grid grid-cols-[5.5rem_1fr] gap-y-1 sm:grid-cols-[6rem_1fr] sm:gap-y-2">
      <span class="text-orange-400">help</span><span class="text-zinc-400">Show available commands</span>
      <span class="text-orange-400">skills</span><span class="text-zinc-400">View my technical skills</span>
      <span class="text-orange-400">projects</span><span class="text-zinc-400">See my projects</span>
      <span class="text-orange-400">contact</span><span class="text-zinc-400">Get my contact information</span>
      <span class="text-orange-400">clear</span><span class="text-zinc-400">Clear the terminal</span>
      <span class="text-orange-400">github</span><span class="text-zinc-400">Open my GitHub profile</span>
      <span class="text-orange-400">linkedin</span><span class="text-zinc-400">Open my LinkedIn profile</span>
      <span class="text-orange-400">easteregg</span><span class="text-zinc-400">???</span>
    </div>
  </div>`

const commandSkills = `
  <div class="space-y-3">
    <p class="font-bold text-green-400">Technical Skills:</p>
    <div class="grid gap-4 sm:grid-cols-2">
      <div><p class="font-semibold text-orange-400">Frontend</p><p class="text-zinc-300">React, React Native, Remix, Next.js, TypeScript, Tailwind CSS, state management and data fetching.</p></div>
      <div><p class="font-semibold text-orange-400">Backend</p><p class="text-zinc-300">Node.js, PostgreSQL, Prisma, Drizzle, GraphQL, REST, AWS and Docker.</p></div>
      <div><p class="font-semibold text-orange-400">Testing</p><p class="text-zinc-300">Vitest, Testing Library, Cypress and Playwright.</p></div>
      <div><p class="font-semibold text-orange-400">Leadership</p><p class="text-zinc-300">Architecture, CI/CD, technical leadership and agile delivery.</p></div>
    </div>
  </div>`

const commandProjects = `
  <div class="space-y-3">
    <p class="font-bold text-green-400">Featured Projects:</p>
    <p><strong class="text-orange-400">MAPflow</strong> — Clinical service platform for pharmacies.</p>
    <p><strong class="text-orange-400">BestOfAI</strong> — A large, searchable AI tools database.</p>
    <p><strong class="text-orange-400">athlete.ai</strong> — Sports highlight creation for athletes.</p>
    <p><strong class="text-orange-400">PolicyScout</strong> — Dynamic insurance technology platform.</p>
    <p><a href="https://github.com/theskillwithin" target="_blank" rel="noopener noreferrer" class="text-orange-400 underline decoration-orange-400/40 underline-offset-2 hover:decoration-orange-400">View more on GitHub ↗</a></p>
  </div>`

const commandContact = `
  <p class="text-zinc-300">📧 Email: <a class="text-orange-400 underline decoration-orange-400/40 underline-offset-2 hover:decoration-orange-400" href="mailto:contact@austinpeterson.dev">contact@austinpeterson.dev</a><br />
  💼 LinkedIn: <a class="text-orange-400 underline decoration-orange-400/40 underline-offset-2 hover:decoration-orange-400" href="https://www.linkedin.com/in/austin-peterson-5377a665/" target="_blank" rel="noopener noreferrer">austin-peterson ↗</a></p>`

function triggerConfetti(signal: AbortSignal) {
  void import('canvas-confetti').then(({ default: confetti }) => {
    if (signal.aborted) return
    const duration = 3_000
    const end = Date.now() + duration
    const interval = window.setInterval(() => {
      const remaining = end - Date.now()
      if (remaining <= 0 || signal.aborted) {
        window.clearInterval(interval)
        return
      }
      const particles = 50 * (remaining / duration)
      const random = (min: number, max: number) =>
        Math.random() * (max - min) + min
      const options = {
        particleCount: particles,
        startVelocity: 30,
        spread: 360,
        ticks: 60,
        zIndex: 60,
        disableForReducedMotion: true,
      }
      confetti({
        ...options,
        origin: { x: random(0.1, 0.3), y: Math.random() - 0.2 },
      })
      confetti({
        ...options,
        origin: { x: random(0.7, 0.9), y: Math.random() - 0.2 },
      })
    }, 250)
    signal.addEventListener('abort', () => window.clearInterval(interval), {
      once: true,
    })
  })
}

function initializeTerminals() {
  const reducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)',
  ).matches

  for (const terminal of document.querySelectorAll<HTMLElement>(
    '[data-terminal]',
  )) {
    const log = terminal.querySelector<HTMLElement>('[data-terminal-log]')
    const scroll = terminal.querySelector<HTMLElement>('[data-terminal-scroll]')
    const form = terminal.querySelector<HTMLFormElement>('[data-terminal-form]')
    const input = terminal.querySelector<HTMLInputElement>(
      '[data-terminal-input]',
    )
    if (!log || !scroll || !form || !input) continue

    const history: string[] = []
    let historyIndex = -1

    const append = (command: string, output: string, error = false) => {
      const entry = document.createElement('div')
      entry.className = 'mb-4'
      entry.dataset.terminalEntry = 'true'
      const prompt = document.createElement('div')
      prompt.className = 'flex items-center'
      prompt.innerHTML =
        '<span class="text-green-400" aria-hidden="true">➜</span><span class="ml-2 text-orange-400" aria-hidden="true">~</span><span class="ml-2 text-zinc-300" aria-hidden="true">$</span>'
      const commandText = document.createElement('span')
      commandText.className = error ? 'ml-2 text-red-400' : 'ml-2 text-zinc-100'
      commandText.textContent = command
      prompt.append(commandText)
      entry.append(prompt)
      if (output) {
        const result = document.createElement('div')
        result.className = 'mt-2 ml-6 text-zinc-300'
        result.innerHTML = output
        entry.append(result)
      }
      log.append(entry)
      // Measure before the entry animation offsets it. Scroll to the bottom
      // unless that would push the new entry's first line out of view; then
      // pin the entry's top instead (typing scrolls back to the prompt).
      const entryTop =
        scroll.scrollTop +
        entry.getBoundingClientRect().top -
        scroll.getBoundingClientRect().top
      scroll.scrollTop = Math.min(
        scroll.scrollHeight - scroll.clientHeight,
        entryTop - 8,
      )
      if (!reducedMotion) {
        entry.animate(
          [
            { opacity: 0, transform: 'translateY(10px)' },
            { opacity: 1, transform: 'translateY(0)' },
          ],
          { duration: 180, easing: 'ease-out' },
        )
      }
    }

    const run = (rawCommand: string) => {
      const command = rawCommand.trim()
      const normalized = command.toLowerCase()
      if (!normalized) return
      history.push(command)
      historyIndex = -1

      if (normalized === 'clear') {
        log.replaceChildren()
        return
      }
      if (normalized === 'help') return append(command, commandHelp)
      if (normalized === 'skills') return append(command, commandSkills)
      if (normalized === 'projects') return append(command, commandProjects)
      if (normalized === 'contact') return append(command, commandContact)
      if (normalized === 'github') {
        window.open(
          'https://github.com/theskillwithin',
          '_blank',
          'noopener,noreferrer',
        )
        return append(command, 'Opening GitHub profile…')
      }
      if (normalized === 'linkedin') {
        window.open(
          'https://www.linkedin.com/in/austin-peterson-5377a665/',
          '_blank',
          'noopener,noreferrer',
        )
        return append(command, 'Opening LinkedIn profile…')
      }
      if (normalized === 'easteregg') {
        triggerConfetti(signal)
        return append(
          command,
          '<p class="text-green-400">🎉 You found an easter egg!</p><pre class="mt-2 text-orange-400">    _             _   _\n   / \\  _   _ ___| |_(_)_ __\n  / _ \\| | | / __| __| | \'_ \\</pre><p class="mt-2 text-zinc-300">Wow, look at all that confetti! 🎊</p>',
        )
      }
      append(
        command,
        'Command not found. Type &quot;help&quot; to see available commands.',
        true,
      )
    }

    form.addEventListener(
      'submit',
      (event) => {
        event.preventDefault()
        run(input.value)
        input.value = ''
      },
      { signal },
    )
    // Typing brings the prompt back into view after reading a long entry.
    input.addEventListener(
      'input',
      () => {
        scroll.scrollTop = scroll.scrollHeight
      },
      { signal },
    )
    input.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'ArrowUp' && history.length) {
          event.preventDefault()
          historyIndex = Math.min(historyIndex + 1, history.length - 1)
          input.value = history[history.length - 1 - historyIndex] ?? ''
        } else if (event.key === 'ArrowDown' && historyIndex >= 0) {
          event.preventDefault()
          historyIndex -= 1
          input.value =
            historyIndex < 0
              ? ''
              : (history[history.length - 1 - historyIndex] ?? '')
        }
      },
      { signal },
    )
    terminal.addEventListener(
      'click',
      (event) => {
        if (
          event.target instanceof HTMLAnchorElement ||
          window.getSelection()?.toString()
        ) {
          return
        }
        input.focus()
      },
      { signal },
    )
  }
}

function initializeConsoleMessage() {
  if (!document.querySelector('[data-console-message]')) return
  console.info(
    '%c👋 Hey there, fellow developer!',
    'font-size: 24px; font-weight: bold; color: #F97316;',
  )
  console.info(
    `%cLooking for a Senior Software Engineer? Let's chat! contact@austinpeterson.dev`,
    'font-size: 14px; color: #71717A;',
  )
}

function initializeAnalytics() {
  if (document.body.dataset.vercel !== 'true') return
  void import('@vercel/analytics')
    .then(({ inject }) => inject({ framework: 'remix' }))
    .catch(() => {})
  void import('@vercel/speed-insights')
    .then(({ injectSpeedInsights }) =>
      injectSpeedInsights({ framework: 'remix' }),
    )
    .catch(() => {})
}

function initializePlaygroundWhenVisible() {
  const playground = document.querySelector<HTMLElement>('[data-playground]')
  if (!playground) return

  let started = false
  const start = () => {
    if (started || signal.aborted) return
    started = true
    void import('./playground-runtime.ts')
      .then(({ initializePlaygrounds }) => initializePlaygrounds(signal))
      .catch((error) => {
        console.error('Could not initialize article playground', error)
        for (const status of document.querySelectorAll<HTMLElement>(
          '[data-playground-status]',
        )) {
          status.textContent = 'Interactive preview unavailable'
        }
      })
  }

  if (!('IntersectionObserver' in window)) {
    start()
    return
  }

  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return
      observer.disconnect()
      start()
    },
    { rootMargin: '600px 0px' },
  )
  observer.observe(playground)
  signal.addEventListener('abort', () => observer.disconnect(), { once: true })
}

try {
  initializeTheme()
  initializeHeader()
  initializeMobileNavigation()
  initializeNavigation()
  initializeIntentPrefetch()
  initializeTilt()
  initializeTerminals()
  initializeConsoleMessage()
  initializeAnalytics()
  initializePlaygroundWhenVisible()
} finally {
  document.documentElement.dataset.hydrated = 'true'
}
